import { Card, CardProps } from '@mui/material'
import clsx from 'clsx'
import React, { useContext, useEffect } from 'react'
import { ProductData } from '../../common/props'
import { convertCategoryNames, priceFormatPartial, SiteContext } from '../../utils/common'
import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import ProductImage from '../ProductImage/ProductImage'
import RawHtml from '../RawHtml/RawHtml'
import SecondaryBadge from '../SecondaryBadge/SecondaryBadge'
import useStyles, { MAX_CARD_WIDTH } from './ProductCard.styles'
import { useRouter } from 'next/router'
import { useLocalStorage } from '../../utils/utilityCustomHooks'
import { PRODUCT_QUERY_PARAMS } from '../../utils/constants'

export const SECONDARY_BADGE_TWO_LINES = 70
export const SECONDARY_BADGE_SINGLE_LINE_MOBILE = 30
export const SECONDARY_BADGE_SINGLE_LINE_DESKTOP = 38
const imageWidths = [MAX_CARD_WIDTH]

export type Props = {
  product: ProductData
  imageCarousel?: boolean
  cardDisplayType?: 'VERBOSE' | 'CONCISE' | 'SIMPLE' | 'DELIVERY_PASS'
  hideBorder?: boolean
  className?: string
  highlightTextMatch?: string
  addToBasket?: (sku: string) => Promise<any>
  index: number
  setHeight?: () => void
  secondaryBadgeRef?: React.RefObject<HTMLDivElement>
  handleBasketLineItem?: (url: string) => void
  isFromBasket?: boolean
  isFromUnavailableSuggest?: boolean
  isFromSearchCard?: boolean
  isFromMegaNavDesktop?: boolean
  isFromMegaNavMobile?: boolean
  hidSecondaryBadge?: boolean
  textContentClasses?: string
  priority?: boolean
  isFromSearchOverlay?: boolean
  isFromInitialSearch?: boolean
  onProductClick?: (id: string, queryId?: string, objectId?: string, position?: string) => void
  pdpRecommendationAnalytics?: (name: string) => void
}

const BorderlessCard = (props: CardProps): JSX.Element => (
  <div {...(props as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>)}>
    {props.children}
  </div>
)

const ProductCard = (props: Props) => {
  const router = useRouter()
  const {
    product: {
      categoryNames,
      categoryUsps,
      description,
      images,
      key,
      name,
      price,
      promotionalText,
      sku,
      wasPrice,
      objectId,
      position,
      queryId,
      queryType,
      indexName,
      isProductOutOfStock,
      hasProductBadge,
      heightOfBadge,
      firstDeliveryMessage,
      lastDeliveryMessage,
    },
    imageCarousel,
    cardDisplayType = 'CONCISE',
    hideBorder,
    className,
    highlightTextMatch,
    addToBasket,
    index,
    setHeight,
    handleBasketLineItem,
    isFromBasket = false,
    isFromUnavailableSuggest,
    isFromSearchCard,
    hidSecondaryBadge,
    textContentClasses,
    isFromMegaNavDesktop,
    isFromMegaNavMobile,
    isFromSearchOverlay,
    isFromInitialSearch,
    onProductClick,
    pdpRecommendationAnalytics,
  } = props

  const { isFlyingFlowers, subject } = useContext(SiteContext)
  const { saveToLocalStorage } = useLocalStorage()
  const getProductSelectedFrom = () => {
    const routerPath = router.asPath
    const paths = [
      { path: isFromUnavailableSuggest, value: 'product_suggestions' },
      { path: isFromMegaNavDesktop, value: 'mega_nav_desktop' },
      { path: isFromMegaNavMobile, value: 'mega_nav_mobile' },
      { path: isFromSearchCard, value: 'search_results' },
      { path: isFromSearchOverlay, value: 'search_overlay' },
      { path: isFromInitialSearch, value: 'initial_search' },
      { path: routerPath === '/' && (!isFromMegaNavDesktop || !isFromMegaNavMobile), value: 'home_page' },
      { path: routerPath?.includes('/product'), value: 'pdp_page' },
      { path: routerPath?.includes('/category'), value: routerPath?.split('/').slice(-1).toString() },
      { path: routerPath?.includes('/flower-delivery'), value: 'location_page' },
      { path: routerPath?.includes('/search'), value: 'pcc_search' },
    ]

    return paths.find(({ path }) => path)?.value
  }
  const isProductSelectedFrom = getProductSelectedFrom()
  const url = props.product.url

  const { classes } = useStyles()
  const buyDeliveryPass = async (e: React.SyntheticEvent) => {
    e.stopPropagation()
    if (addToBasket && sku) addToBasket(sku)
  }

  // Card Display Types
  const isDeliveryPass = cardDisplayType === 'DELIVERY_PASS'
  const textContentClass = cardDisplayType === 'SIMPLE' ? classes.textContentSimple : undefined
  const showBadge = cardDisplayType !== 'SIMPLE' && !isDeliveryPass
  const showDetails = cardDisplayType === 'VERBOSE'
  const showPromotion = cardDisplayType !== 'SIMPLE'
  const usePartialPrice = cardDisplayType === 'SIMPLE'
  const Component = hideBorder ? BorderlessCard : Card
  const isProductLink = !isDeliveryPass && !isFromBasket

  const secondaryBadge = images[0]?.secondaryBadge ?? undefined
  const imageAltText = name && !isDeliveryPass ? name : images[0]?.altText ?? ''

  // Data for GA4 click event
  const analyticsItem = {
    item_id: key,
    item_name: name,
    price: price?.formatted?.slice(1),
    currency: price?.currencyCode,
    quantity: 1,
    index,
    ...convertCategoryNames(categoryNames),
  }

  const handleClick = (e: any) => {
    const queryParams = {
      queryId,
      objectId,
      category: subject?.category?.key,
      productOrderedFrom: isProductSelectedFrom,
      index: indexName,
    }
    if (isFromMegaNavDesktop) {
      e.stopPropagation()
    }
    pdpRecommendationAnalytics?.(name)
    sku && onProductClick?.(sku, queryId, objectId, position)

    saveToLocalStorage(PRODUCT_QUERY_PARAMS, JSON.stringify(queryParams))
  }
  const handleClickForUpdateBasket = (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (isFromBasket && handleBasketLineItem) {
      handleBasketLineItem(url)
    }
  }

  useEffect(() => {
    setHeight && window?.addEventListener('resize', setHeight)
    return () => setHeight && window.removeEventListener('resize', setHeight)
  }, [setHeight, hasProductBadge])

  const Wrapper = isProductLink ? 'a' : React.Fragment
  const wrapperProps = isProductLink ? { href: url, className: classes.linkWrapper, onClick: handleClick, id: sku } : {}
  const ifProductBadge = showBadge ? images[0]?.badge : undefined
  const ffSalesBadge = showBadge && isFlyingFlowers ? images[0]?.ffSalesBadge : undefined
  const wasPriceValue = wasPrice ?? ''
  const breakdownWasPrice = wasPriceValue?.split(',')
  return (
    <Wrapper {...wrapperProps}>
      <Component
        className={clsx(
          className,
          classes.productCard,
          !hideBorder && classes.productCardBorder,
          isProductLink && classes.cardClickable,
          isProductOutOfStock && classes.disableProductCard,
          isFromBasket ? classes.productSuggestion : ''
        )}
        data-item={JSON.stringify(analyticsItem)}
        // data for Algolia Insights click event
        data-insights-object-id={objectId}
        data-insights-position={position}
        data-insights-query-id={queryId}
        data-insights-query-type={queryType}
        data-insights-index-name={indexName}
        data-hj-ignore-attributes
        onClick={isFromBasket ? handleClickForUpdateBasket : undefined}
        data-testid={sku}
      >
        {images && (
          <ProductImage
            // Add a gutter manually when only one image but other cards could have a carousel, to line up the heights as the product images are square
            classNames={
              !(imageCarousel && cardDisplayType !== 'DELIVERY_PASS') && isDeliveryPass
                ? { container: classes.deliveryPassImage }
                : undefined
            }
            altText={imageAltText}
            imageUrl={images[0]?.url}
            maxWidth={MAX_CARD_WIDTH}
            imageWidths={imageWidths}
            badge={ifProductBadge}
            badgeSmall
            salesBadge={ffSalesBadge}
            isFromProductCard={true}
            loading="lazy"
            priority={false}
          />
        )}

        <div className={clsx(classes.textContent, textContentClass, textContentClasses)} data-hj-ignore-attributes>
          <div
            style={{
              height: hasProductBadge && !!heightOfBadge ? `${heightOfBadge}px` : 'initial',
            }}
          >
            {secondaryBadge && !hidSecondaryBadge && (
              <SecondaryBadge
                secondaryBadge={secondaryBadge}
                isFromSimpleCard={isFromUnavailableSuggest || isFromSearchCard}
                isFromSearchCard={isFromSearchCard}
              />
            )}
          </div>

          <div className={clsx(classes.topSectionContainer)}>
            {isDeliveryPass ? (
              <>
                <h4 className={classes.deliveryPassProductName}>{name}</h4>
                <p className={classes.deliveryPassPriceContainer}>
                  <span className={classes.deliveryPassPrice}>only {price.formatted}</span>
                </p>
                <RawHtml className={classes.deliveryPassPromoText} content={description} />
              </>
            ) : (
              <>
                {isProductLink ? (
                  <h4 className={classes.productName}>{name}</h4>
                ) : (
                  <Link
                    label={name}
                    url={url}
                    highlightTextMatch={highlightTextMatch}
                    className={classes.productName}
                  />
                )}
                {showDetails && (
                  <>
                    {!!categoryUsps?.length &&
                      categoryUsps.slice(0, 4).map(
                        (categoryUsp, index) =>
                          !!categoryUsp && (
                            <div key={index} className={classes.container}>
                              <Icon icon="spa" className={clsx(classes.icon, isFlyingFlowers && classes.iconClass)} />
                              <div className={classes.variantText}>{categoryUsp}</div>
                            </div>
                          )
                      )}
                  </>
                )}
                {firstDeliveryMessage && (
                  <div className={classes.container}>
                    <Icon icon="local_shipping" className={clsx(classes.icon, isFlyingFlowers && classes.iconClass)} />
                    <div className={classes.variantText}>{firstDeliveryMessage}</div>
                  </div>
                )}
                {lastDeliveryMessage && (
                  <div className={classes.container}>
                    <Icon icon="local_shipping" className={clsx(classes.icon, isFlyingFlowers && classes.iconClass)} />
                    <div className={classes.variantText}>{lastDeliveryMessage}</div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className={classes.bottomSectionContainer}>
            {!isDeliveryPass && (
              <p className={clsx(classes.priceContainer, breakdownWasPrice ? classes.productCardUKWasPrice : '')}>
                <span className={classes.price}>
                  {usePartialPrice && !!price.currencyCode ? priceFormatPartial(price) : price.formatted}
                </span>
                {showPromotion && promotionalText && <span className={classes.promoText}>{promotionalText}</span>}
              </p>
            )}
            {wasPrice && isFlyingFlowers ? (
              <p className={classes.wasPrice}>{wasPrice}</p>
            ) : (
              breakdownWasPrice && (
                <div
                  className={clsx(
                    classes.allWasPrice,
                    isFromSearchCard || isFromSearchOverlay || isFromMegaNavDesktop ? classes.searchCardWasPrice : ''
                  )}
                >
                  <span className={classes.strikeThroughPrice}>{breakdownWasPrice[0]}</span>&nbsp;
                  <span>{breakdownWasPrice[1]}</span>
                </div>
              )
            )}
            {cardDisplayType === 'DELIVERY_PASS' && !isFlyingFlowers && (
              <Button fullWidth={true} onClick={buyDeliveryPass} variant="tertiary" title={`Buy ${name}`} thin={true} />
            )}
          </div>
        </div>
      </Component>
    </Wrapper>
  )
}

export default ProductCard
